import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';

import {
  LocationImportFlow,
  locationImportHasFile,
  locationImportIsReady,
} from '../../components/LocationImportFlow';
import type { OnboardingDraft, OnboardingStepProps } from '../types';

// Step 4 of onboarding (between member import and payments). UX:
//   - No file picked → call-to-action + Skip-by-Continue (the wizard's
//     Slutför / Continue button is the only way forward).
//   - File picked → AI mapping table + Places-resolved preview. The wizard's
//     submit handler reads draft.locationImport.resolvedRows after
//     createOrganization and POSTs them to bulkImportLocations.
//   - Validity gate (locationsIsValid): a picked-but-unresolved file blocks
//     Continue — same shape as the member-import step.
export function Locations({ draft, update }: OnboardingStepProps) {
  const auth = useAuth();
  const { t, i18n } = useTranslation('common');
  const token = auth.user?.access_token ?? null;
  const language = i18n.language || 'en';
  const region = useMemo(() => regionFromLocale(language), [language]);

  return (
    <div className="form" style={{ gap: '1rem' }}>
      <p>{t('onboarding.locations.body')}</p>

      <LocationImportFlow
        token={token}
        language={language}
        region={region}
        state={draft.locationImport}
        onChange={(patch) =>
          update({ locationImport: { ...draft.locationImport, ...patch } })
        }
      />

      <p className="form__hint">{t('onboarding.locations.skipHint')}</p>
    </div>
  );
}

// locationsIsValid gates the wizard's Continue / Slutför button:
//   - No file picked → true (skipping is fine)
//   - File picked but addresses still resolving / mapping incomplete → false
//   - File picked, mapping + resolution complete → true
export const locationsIsValid = (draft: OnboardingDraft): boolean => {
  if (!locationImportHasFile(draft.locationImport)) return true;
  return locationImportIsReady(draft.locationImport);
};

function regionFromLocale(locale: string): string {
  const lower = locale.toLowerCase();
  if (lower.startsWith('sv')) return 'SE';
  if (lower.startsWith('no') || lower.startsWith('nb') || lower.startsWith('nn')) return 'NO';
  if (lower.startsWith('da')) return 'DK';
  if (lower.startsWith('fi')) return 'FI';
  return 'SE';
}
